import React, { useEffect, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ViewProjectReport } from "../../Redux/Actions/projectReportActions";
import MainLayout from "../../Layout/mainLayout";
import "./viewProjectReport.css";

const SCORE_FIELDS = [
    { key: "location_advantage", label: "Location Advantage" },
    { key: "future_growth", label: "Future Growth" },
    { key: "trust_score", label: "Trust Score" },
    { key: "Livability_score", label: "Livability Score" },
    { key: "assurance_score", label: "Assurance Score" },
];

const ViewProjectReportPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { projectReport: data, loading } = useSelector((state) => state.projectReport);

    useEffect(() => {
        if (!id) return;
        if (data?._id === id) return;
        dispatch(ViewProjectReport(id)).catch(() => {
            toast.error("Failed to load project report details.");
        });
    }, [id, dispatch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "Not specified";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    }, []);

    const reportPdfValue = data?.report_pdf?.value || "";
    const reportPdfNotes = useMemo(() => {
        const note = data?.report_pdf?.note;
        return Array.isArray(note) ? note : [];
    }, [data]);

    const totalNotesCount = useMemo(() => {
        if (!data) return 0;
        const scoreNotesCount = SCORE_FIELDS.reduce((sum, { key }) => {
            const note = data[key]?.note;
            return sum + (Array.isArray(note) ? note.length : 0);
        }, 0);
        return scoreNotesCount + reportPdfNotes.length;
    }, [data, reportPdfNotes]);

    if (loading) {
        return (
            <MainLayout>
                <div className="vbg-loader-wrap">
                    <div className="vbg-loader" />
                    <p className="vbg-loader-text">Loading report details…</p>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vbg-wrapper">
                <div className="vbg-header">
                    <div className="vbg-header-left">
                        <Link to="/project_report" className="vbg-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vbg-header-divider" />
                        <div>
                            <div className="vbg-breadcrumb">Project Report / List / View</div>
                            <h1 className="vbg-title">Report Details</h1>
                        </div>
                    </div>
                </div>

                {data && (
                    <>
                        <div className="vbg-hero-card">
                            <div className="vbg-hero-left">
                                <span className="vbg-hero-label">Project Name</span>
                                <span className="vbg-hero-value">{data.project?.project_name || data.project || "—"}</span>
                            </div>
                            <div className="vbg-hero-meta">
                                <div className="vbg-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    {formatDate(data.created_at)}
                                </div>
                                {data.project?.business_type && (
                                    <span className="vbg-category-badge">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                        </svg>
                                        {data.project.business_type.charAt(0).toUpperCase() + data.project.business_type.slice(1)}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="vbg-grid-layout">
                            <div className="vbg-col">
                                <div className="vbg-card">
                                    <div className="vbg-card-header">
                                        <span className="vbg-card-badge">INFO</span>
                                        <h3 className="vbg-card-title">Report Information</h3>
                                    </div>
                                    <div className="vbg-info-grid">
                                        <div className="vbg-info-item">
                                            <span className="vbg-info-label">Project</span>
                                            <span className="vbg-info-value">{data.project?.project_name || "—"}</span>
                                        </div>
                                        <div className="vbg-info-item">
                                            <span className="vbg-info-label">Business Type</span>
                                            <span className="vbg-info-value">{data.project?.business_type || "—"}</span>
                                        </div>
                                        <div className="vbg-info-item">
                                            <span className="vbg-info-label">Created At</span>
                                            <span className="vbg-info-value">{formatDate(data.created_at)}</span>
                                        </div>
                                        <div className="vbg-info-item">
                                            <span className="vbg-info-label">Status</span>
                                            <span className="vbg-info-value">{data.status || "—"}</span>
                                        </div>
                                        <div className="vbg-info-item">
                                            <span className="vbg-info-label">Total Notes</span>
                                            <span className="vbg-info-value">{totalNotesCount}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="vbg-card">
                                    <div className="vbg-card-header">
                                        <span className="vbg-card-badge">PDF</span>
                                        <h3 className="vbg-card-title">Report PDF</h3>
                                    </div>
                                    {reportPdfValue ? (
                                        <a href={reportPdfValue} target="_blank" rel="noreferrer" className="vbg-back-btn" style={{ display: "inline-flex" }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                                            </svg>
                                            View PDF
                                        </a>
                                    ) : (
                                        <p className="vbg-no-data">No PDF uploaded.</p>
                                    )}
                                    {reportPdfNotes.length > 0 && (
                                        <div className="vbg-pdf-notes">
                                            {reportPdfNotes.map((note, index) => (
                                                <div key={index} className="vbg-pdf-note-item">
                                                    <span className="vbg-pdf-note-label">Note {index + 1}</span>
                                                    <p className="vbg-pdf-note-text">{note}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="vbg-card">
                                <div className="vbg-card-header">
                                    <span className="vbg-card-badge">SCORES</span>
                                    <h3 className="vbg-card-title">Report Scores</h3>
                                </div>
                                <div className="vbg-score-list">
                                    {SCORE_FIELDS.map(({ key, label }) => {
                                        const field = data[key] || {};
                                        const fieldNotes = Array.isArray(field.note) ? field.note : [];
                                        return (
                                            <div key={key} className="vbg-score-item">
                                                <div className="vbg-score-row">
                                                    <span className="vbg-score-label">{label}</span>
                                                    <span className="vbg-score-value">{field.value ?? "—"}</span>
                                                </div>
                                                {fieldNotes.length > 0 ? (
                                                    <ul className="vbg-score-notes">
                                                        {fieldNotes.map((note, index) => (
                                                            <li key={index}>{note}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="vbg-no-data" style={{ margin: "4px 0 0" }}>No notes available.</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!data && !loading && (
                    <p className="vbg-no-data">Project report not found.</p>
                )}
            </div>
        </MainLayout>
    );
};

export default ViewProjectReportPage;